import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const EDITABLE_FIELDS = [
  { id: 'property_address', label: 'Property Address' },
  { id: 'client_code', label: 'Client Code' },
  { id: 'client_product', label: 'Product Type' },
  { id: 'parcel', label: 'Parcel / APN' },
  { id: 'buyer_name', label: 'Buyer Name' },
  { id: 'seller_name', label: 'Seller Name' },
  { id: 'effective_date', label: 'Effective Date' },
  { id: 'notes', label: 'Order Notes' },
  { id: 'other', label: 'Other' },
];

const EditRequestModal = ({ isOpen, onClose, orderData, onSuccess }) => {
  const [fieldName, setFieldName] = useState('');
  const [requestedValue, setRequestedValue] = useState('');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canSubmit = fieldName && requestedValue.trim() && reason.trim() && !isSubmitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/edit-requests`,
        {
          order_id: orderData.id,
          field_name: fieldName,
          requested_value: requestedValue.trim(),
          reason: reason.trim()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Edit request submitted');
      setFieldName('');
      setRequestedValue('');
      setReason('');
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !orderData) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.4)' }}
      onClick={onClose}
      data-testid="edit-request-modal-backdrop"
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4"
        onClick={(e) => e.stopPropagation()}
        data-testid="edit-request-modal"
      >
        {/* Header */}
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Request Correction</h3>
            <p className="text-xs text-gray-500">{orderData.order_number || orderData.orderNumber}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="close-edit-request-modal">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-3">
          {/* Field select */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Field</label>
            <select
              value={fieldName}
              onChange={(e) => setFieldName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
              data-testid="edit-request-field-select"
            >
              <option value="">Select field...</option>
              {EDITABLE_FIELDS.map(f => (
                <option key={f.id} value={f.id}>{f.label}</option>
              ))}
            </select>
          </div>

          {/* Requested value */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Correct Value</label>
            <input
              type="text"
              value={requestedValue}
              onChange={(e) => setRequestedValue(e.target.value)}
              placeholder="What it should be..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
              maxLength={500}
              data-testid="edit-request-value-input"
            />
          </div>

          {/* Reason */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Reason</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why this needs changing..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-amber-500 focus:border-amber-500"
              maxLength={500}
              data-testid="edit-request-reason-input"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-3 py-2 text-sm text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
              disabled={isSubmitting}
              data-testid="cancel-edit-request"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="flex-1 px-3 py-2 text-sm text-white bg-amber-500 rounded-md hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed font-medium"
              data-testid="submit-edit-request"
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditRequestModal;
